import { z } from 'zod'

export type ImportedGoodFormData = {
  customId?: string
  importDate: string
  description?: string
  cnCode: string
  country: string
  netMassInTonnes: number
  previousCustomsProcedure?: string
  requestedCustomsProcedure?: string
  inwardProcessedNetMassInTonnes?: number
  inwardProcessingInformation?: {
    memberState: string
    inwardProcessingWaiver: boolean
    authorisation: string
    globalisationTimeStart: string
    globalisationTimeEnd: string
    billOfDischargeDeadline: string
  }[]
  isUsingDefaultEmissions: boolean
  defaultSpecificDirectEmbeddedEmissions?: string
  defaultSpecificIndirectEmbeddedEmissions?: string
  defaultElectricityConsumptionMWh?: string
  importerId?: string
  partnershipId?: string
  installationId?: string
  supplierEmissionsReportId?: string
}

const inwardProcessingInformationSchema = z
  .object({
    memberState: z.string().min(2).max(2),
    inwardProcessingWaiver: z.boolean(),
    authorisation: z.string().max(512),
    globalisationTimeStart: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, { error: 'Invalid date format' }),
    globalisationTimeEnd: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { error: 'Invalid date format' }),
    billOfDischargeDeadline: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, { error: 'Invalid date format' })
  })
  .refine(
    (data: { globalisationTimeStart: string; globalisationTimeEnd: string }) =>
      new Date(data.globalisationTimeStart) <= new Date(data.globalisationTimeEnd),
    {
      error: 'Globalisation time start must be less or equal to the Globalisation time end',
      path: ['globalisationTimeStart']
    }
  )
  .refine(
    (data: { globalisationTimeEnd: string; billOfDischargeDeadline: string }) => {
      const globalisationEnd = new Date(data.globalisationTimeEnd)
      const billDeadline = new Date(data.billOfDischargeDeadline)
      const maxDeadline = new Date(globalisationEnd)
      maxDeadline.setDate(globalisationEnd.getDate() + 30)
      return billDeadline >= globalisationEnd && billDeadline <= maxDeadline
    },
    {
      error:
        'The deadline for the bill of discharge must be equal or greater (max. 30 days) than the globalisation time end',
      path: ['billOfDischargeDeadline']
    }
  )

const importedGoodFormSchema = z.object({
  customId: z.string().optional(),
  importDate: z.string().min(1),
  description: z.string().optional(),
  cnCode: z.string().min(1),
  country: z.string().min(2).max(2),
  netMassInTonnes: z.number().gt(0),
  isUsingDefaultEmissions: z.boolean(),
  previousCustomsProcedure: z.string().optional(),
  requestedCustomsProcedure: z.string().optional(),
  inwardProcessedNetMassInTonnes: z.number().optional(),
  inwardProcessingInformation: z.array(inwardProcessingInformationSchema).optional(),
  defaultSpecificDirectEmbeddedEmissions: z.string().optional(),
  defaultSpecificIndirectEmbeddedEmissions: z.string().optional(),
  defaultElectricityConsumptionMWh: z.string().optional(),
  importerId: z.string().optional(),
  partnershipId: z.string().optional(),
  installationId: z.string().optional(),
  supplierEmissionsReportId: z.string().optional()
})

export const validateImportedGoodForm = (data: ImportedGoodFormData) => {
  return importedGoodFormSchema.safeParse(data)
}
